import type { EntityFacets } from '@shared/types/status-filter.types';

export interface FilterParams {
  query?: string;
  status?: string;
  statusFilter?: string;
  filters?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface FilterOptions {
  searchFields?: string[];
  statusMappings?: Record<string, readonly string[]>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  customFilters?: Record<string, (items: any[], value: unknown) => any[]>;
}

export class FilteringUtils {
  static applySearch<T>(
    data: T[],
    query: string | undefined,
    searchFields: string[]
  ): T[] {
    if (!query?.trim()) return data;

    const searchTerm = query.toLowerCase().trim();

    return data.filter(item =>
      searchFields.some(field => {
        const value = this.getNestedFieldValue(item as Record<string, unknown>, field);
        return String(value).toLowerCase().includes(searchTerm);
      })
    );
  }

  static applyStatusFilter<T>(
    data: T[],
    statusFilter: string | undefined,
    statusMappings?: Record<string, readonly string[]>
  ): T[] {
    if (!statusFilter || statusFilter === 'all') {
      return data;
    }

    if (statusMappings?.[statusFilter]) {
      const mappedStatuses = statusMappings[statusFilter];
      return data.filter(item => {
        const itemStatus = this.getNestedFieldValue(item as Record<string, unknown>, 'status');
        return (mappedStatuses as string[]).includes(String(itemStatus));
      });
    }

    return data.filter(item => {
      const itemStatus = this.getNestedFieldValue(item as Record<string, unknown>, 'status');
      return itemStatus === statusFilter;
    });
  }

  static applyArrayFilters<T>(
    data: T[],
    filters: Record<string, unknown> | undefined
  ): T[] {
    if (!filters) return data;

    let filtered = data;

    Object.entries(filters).forEach(([key, value]) => {
      if (Array.isArray(value) && value.length > 0) {
        filtered = filtered.filter(item => {
          const itemValue = this.getNestedFieldValue(item as Record<string, unknown>, key);
          return value.includes(itemValue);
        });
      } else if (value != null && value !== '') {
        filtered = filtered.filter(item => {
          const itemValue = this.getNestedFieldValue(item as Record<string, unknown>, key);
          return itemValue === value;
        });
      }
    });

    return filtered;
  }

  /**
   * Apply range filters (e.g., amount: { min: 100, max: 1000 })
   */
  static applyRangeFilter<T>(
    data: T[],
    fieldName: string,
    rangeFilter: { min?: number; max?: number } | undefined
  ): T[] {
    if (!rangeFilter) return data;

    return data.filter(item => {
      const value = this.getNestedFieldValue(item as Record<string, unknown>, fieldName);
      const numValue = typeof value === 'number' ? value : parseFloat(String(value));

      if (isNaN(numValue)) return false;

      if (rangeFilter.min !== undefined && numValue < rangeFilter.min) return false;
      if (rangeFilter.max !== undefined && numValue > rangeFilter.max) return false;

      return true;
    });
  }

  static applyFilters<T>(
    data: T[],
    params: FilterParams,
    options: FilterOptions = {}
  ): T[] {
    let filtered = data;

    // Handle search filtering
    if (options.searchFields && params.query) {
      const isSerializedFilter = this.isSerializedFilterQuery(params.query);

      if (!isSerializedFilter) {
        filtered = this.applySearch(filtered, params.query, options.searchFields);
      }
    }

    // Handle status filtering - serialized queries take priority
    if (this.isSerializedFilterQuery(params.query)) {
      const statusArray = this.parseSerializedStatusQuery(params.query);
      filtered = this.applyDirectStatusFilter(filtered, statusArray);
    } else if (params.statusFilter || params.status) {
      const statusValue = params.statusFilter || params.status;
      filtered = this.applyStatusFilter(filtered, statusValue, options.statusMappings);
    }

    // Handle array filters
    if (params.filters) {
      filtered = this.applyArrayFilters(filtered, params.filters);
    }

    // Handle custom filters
    if (options.customFilters) {
      Object.entries(options.customFilters).forEach(([key, filterFn]) => {
        const filterValue = params.filters?.[key] || params[key];
        if (filterValue != null) {
          filtered = filterFn(filtered, filterValue);
        }
      });
    }

    return filtered;
  }

  /**
   * Calculate facets from data
   */
  static calculateFacets<T>(
    data: T[],
    facetFields: string[]
  ): EntityFacets {
    const facets: EntityFacets = {};

    facetFields.forEach(field => {
      const counts = data.reduce((acc, item) => {
        const value = this.getNestedFieldValue(item as Record<string, unknown>, field);
        const key = String(value || 'unknown');
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      facets[field] = Object.entries(counts).map(([value, count]) => ({
        value,
        count
      }));
    });

    return facets;
  }

  /**
   * Apply pagination to data
   */
  static paginateData<T>(
    data: T[],
    page: number = 0,
    pageSize: number = 20
  ): { data: T[]; total: number; totalPages: number } {
    const total = data.length;
    const totalPages = Math.ceil(total / pageSize);
    const startIndex = page * pageSize;
    const paginatedData = data.slice(startIndex, startIndex + pageSize);

    return {
      data: paginatedData,
      total,
      totalPages
    };
  }

  static getNestedFieldValue(obj: Record<string, unknown>, fieldPath: string): unknown {
    return fieldPath.split('.').reduce<unknown>((current, field) => {
      if (current != null && typeof current === 'object') {
        const val = (current as Record<string, unknown>)[field];
        return val !== undefined ? val : '';
      }
      return '';
    }, obj);
  }

  /**
   * Check if a query string is a serialized filter query rather than a text search
   * @param query - The query string to check
   * @returns true if this is a serialized filter query
   */
  static isSerializedFilterQuery(query: unknown): query is string {
    return typeof query === 'string' && query.startsWith('//status=');
  }

  /**
   * Parse a serialized status query into an array of status values
   * @param query - The serialized query string (e.g., '//status=active;terminated')
   * @returns Array of status values
   */
  static parseSerializedStatusQuery(query: string): string[] {
    if (!this.isSerializedFilterQuery(query)) {
      return [];
    }

    try {
      const serializedStatuses = query.replace('//status=', '');
      return serializedStatuses.split(';').filter(status => status.trim());
    } catch {
      return [];
    }
  }

  /**
   * Apply direct status filtering using an array of status values
   * @param data - The data to filter
   * @param statusArray - Array of status values to match against
   * @returns Filtered data
   */
  static applyDirectStatusFilter<T>(data: T[], statusArray: string[]): T[] {
    if (!statusArray.length) {
      return data;
    }

    return data.filter(item => {
      const itemStatus = this.getNestedFieldValue(item as Record<string, unknown>, 'status');
      return statusArray.includes(String(itemStatus));
    });
  }
}
